import React from "react";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../prim";

/**
 * Anivaze v3 — skin-aware ANIMAL ACTORS (seed range 1200–1399).
 *
 * Each actor is `actor(x, y, t, scale?, …opts) → JSX`, composed ONLY from the
 * skinnable primitives in `../prim`. Animate via `<g transform>` so hand-drawn
 * seeds stay stable. Pure function of `t` — no state, no Date.now(), no Math.random().
 * Kid-friendly colors. Renders cleanly at t = 0.
 */

// ─── skButterfly ─────────────────────────────────────────────────────────────
/** Wings flap via rotate from t; body gently bobs. Seed range 1200–1219. */
export function skButterfly(x: number, y: number, t: number, scale = 1, color = "#a78bfa"): React.ReactNode {
  const flapAngle = Math.sin(t * 6) * 40;       // wing open/close
  const bob = Math.sin(t * 3) * 4;              // gentle vertical bob
  return (
    <g transform={`translate(${x} ${y + bob}) scale(${scale})`}>
      {/* left upper wing */}
      <g transform={`rotate(${-flapAngle} 0 0)`}>
        <SkEllipse cx={-18} cy={-12} rx={18} ry={12} fill={color} seed={1200} />
      </g>
      {/* right upper wing */}
      <g transform={`rotate(${flapAngle} 0 0)`}>
        <SkEllipse cx={18} cy={-12} rx={18} ry={12} fill={color} seed={1201} />
      </g>
      {/* left lower wing */}
      <g transform={`rotate(${-flapAngle * 0.7} 0 0)`}>
        <SkEllipse cx={-12} cy={8} rx={12} ry={8} fill="#c4b5fd" seed={1202} />
      </g>
      {/* right lower wing */}
      <g transform={`rotate(${flapAngle * 0.7} 0 0)`}>
        <SkEllipse cx={12} cy={8} rx={12} ry={8} fill="#c4b5fd" seed={1203} />
      </g>
      {/* body */}
      <SkEllipse cx={0} cy={0} rx={3} ry={14} fill="#4c1d95" seed={1204} />
      {/* antennae */}
      <SkLine x1={-2} y1={-12} x2={-8} y2={-22} seed={1205} />
      <SkLine x1={2} y1={-12} x2={8} y2={-22} seed={1206} />
      <SkCircle cx={-8} cy={-22} r={2} fill="#fde047" seed={1207} />
      <SkCircle cx={8} cy={-22} r={2} fill="#fde047" seed={1208} />
    </g>
  );
}

// ─── skWorm ──────────────────────────────────────────────────────────────────
/** Segmented worm that inches forward; segments ripple via offset from t. Seed range 1220–1239. */
export function skWorm(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const segCount = 6;
  const segSpacing = 14;
  // inch: forward offset cycles every 2s, bunching segments
  const inch = (t % 2) / 2;  // 0→1 forward stroke
  const stretchX = Math.sin(inch * Math.PI) * 8; // body stretches then contracts
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {Array.from({ length: segCount }, (_, i) => {
        const phase = i / segCount;
        const segX = -(segCount - 1 - i) * segSpacing + stretchX * (1 - phase);
        const segY = Math.sin(t * 4 + phase * Math.PI * 2) * 3;
        const col = i % 2 === 0 ? "#4ade80" : "#86efac";
        return (
          <g key={i} transform={`translate(${segX} ${segY})`}>
            <SkCircle cx={0} cy={0} r={8} fill={col} seed={1220 + i} />
          </g>
        );
      })}
      {/* head */}
      <g transform={`translate(${stretchX * 0} 0)`}>
        <SkCircle cx={0} cy={Math.sin(t * 4) * 3} r={10} fill="#22c55e" seed={1226} />
        <SkCircle cx={-3} cy={Math.sin(t * 4) * 3 - 3} r={2} fill="#166534" seed={1227} />
        <SkCircle cx={3} cy={Math.sin(t * 4) * 3 - 3} r={2} fill="#166534" seed={1228} />
        {/* smile */}
        <SkLine x1={-3} y1={Math.sin(t * 4) * 3 + 3} x2={3} y2={Math.sin(t * 4) * 3 + 5} seed={1229} />
      </g>
    </g>
  );
}

// ─── skCat ───────────────────────────────────────────────────────────────────
/** Sitting cat with ears, eyes, and a tail that flicks. Seed range 1240–1259. */
export function skCat(x: number, y: number, t: number, scale = 1, color = "#f97316"): React.ReactNode {
  const tailSwing = Math.sin(t * 2) * 30;       // tail flick angle
  const blinkT = Math.sin(t * 2);               // blink when near -1
  const eyeRy = blinkT < -0.92 ? 1 : 5;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* tail — pivots from base of body */}
      <g transform={`rotate(${tailSwing} 0 0)`}>
        <SkEllipse cx={-38} cy={10} rx={7} ry={22} fill={color} seed={1240} />
        <SkCircle cx={-38} cy={-10} r={7} fill="#fdba74" seed={1241} />
      </g>
      {/* body */}
      <SkEllipse cx={0} cy={10} rx={30} ry={28} fill={color} seed={1242} />
      {/* head */}
      <SkCircle cx={0} cy={-30} r={22} fill={color} seed={1243} />
      {/* ears */}
      <SkPolygon points={[[-22, -46], [-14, -62], [-6, -46]]} fill={color} seed={1244} />
      <SkPolygon points={[[6, -46], [14, -62], [22, -46]]} fill={color} seed={1245} />
      <SkPolygon points={[[-20, -47], [-14, -58], [-9, -47]]} fill="#fca5a5" seed={1246} />
      <SkPolygon points={[[9, -47], [14, -58], [20, -47]]} fill="#fca5a5" seed={1247} />
      {/* eyes */}
      <SkEllipse cx={-8} cy={-32} rx={5} ry={eyeRy} fill="#15803d" seed={1248} />
      <SkEllipse cx={8} cy={-32} rx={5} ry={eyeRy} fill="#15803d" seed={1249} />
      {/* nose */}
      <SkCircle cx={0} cy={-24} r={2} fill="#fca5a5" seed={1250} />
      {/* whiskers */}
      <SkLine x1={-2} y1={-24} x2={-18} y2={-22} seed={1251} />
      <SkLine x1={-2} y1={-24} x2={-18} y2={-26} seed={1252} />
      <SkLine x1={2} y1={-24} x2={18} y2={-22} seed={1253} />
      <SkLine x1={2} y1={-24} x2={18} y2={-26} seed={1254} />
      {/* belly spot */}
      <SkEllipse cx={0} cy={14} rx={14} ry={16} fill="#fdba74" seed={1255} />
    </g>
  );
}

// ─── skDog ───────────────────────────────────────────────────────────────────
/** Happy dog with wagging tail. Seed range 1260–1279. */
export function skDog(x: number, y: number, t: number, scale = 1, color = "#d97706"): React.ReactNode {
  const wagAngle = Math.sin(t * 7) * 40;   // enthusiastic wag
  const bounce = Math.abs(Math.sin(t * 3.5)) * 3;
  return (
    <g transform={`translate(${x} ${y + bounce}) scale(${scale})`}>
      {/* tail — pivot from back of body */}
      <g transform={`translate(26 0) rotate(${wagAngle})`}>
        <SkEllipse cx={0} cy={-16} rx={5} ry={18} fill={color} seed={1260} />
      </g>
      {/* body */}
      <SkEllipse cx={0} cy={0} rx={32} ry={22} fill={color} seed={1261} />
      {/* head */}
      <SkCircle cx={-26} cy={-18} r={20} fill={color} seed={1262} />
      {/* snout */}
      <SkEllipse cx={-36} cy={-12} rx={10} ry={8} fill="#fbbf24" seed={1263} />
      {/* nose */}
      <SkCircle cx={-40} cy={-14} r={4} fill="#1c1917" seed={1264} />
      {/* eye */}
      <SkCircle cx={-20} cy={-22} r={4} fill="#1c1917" seed={1265} />
      <SkCircle cx={-19} cy={-23} r={1} fill="#ffffff" seed={1266} />
      {/* floppy ear */}
      <SkEllipse cx={-18} cy={-6} rx={10} ry={16} fill="#b45309" seed={1267} />
      {/* legs */}
      <SkRect x={-20} y={14} w={10} h={18} rx={5} fill={color} seed={1268} />
      <SkRect x={-5} y={14} w={10} h={18} rx={5} fill={color} seed={1269} />
      <SkRect x={10} y={14} w={10} h={18} rx={5} fill={color} seed={1270} />
      {/* tongue */}
      <SkEllipse cx={-38} cy={-6} rx={4} ry={6} fill="#fb7185" seed={1271} />
    </g>
  );
}

// ─── skFish ──────────────────────────────────────────────────────────────────
/** Fish whose tail swishes as it swims; optional bubbles. Seed range 1280–1299. */
export function skFish(x: number, y: number, t: number, scale = 1, color = "#38bdf8"): React.ReactNode {
  const tailWag = Math.sin(t * 5) * 18;    // tail oscillation
  const swimX = Math.sin(t * 0.8) * 6;    // gentle lateral drift
  const swimY = Math.sin(t * 1.3) * 4;
  return (
    <g transform={`translate(${x + swimX} ${y + swimY}) scale(${scale})`}>
      {/* tail fin — pivot at body rear */}
      <g transform={`translate(28 0) rotate(${tailWag})`}>
        <SkPolygon points={[[0, 0], [16, -12], [16, 12]]} fill={color} seed={1280} />
      </g>
      {/* body */}
      <SkEllipse cx={0} cy={0} rx={30} ry={16} fill={color} seed={1281} />
      {/* belly highlight */}
      <SkEllipse cx={2} cy={4} rx={18} ry={9} fill="#e0f2fe" seed={1282} />
      {/* dorsal fin */}
      <SkPolygon points={[[-10, -14], [0, -26], [12, -14]]} fill="#0ea5e9" seed={1283} />
      {/* eye */}
      <SkCircle cx={-18} cy={-3} r={5} fill="#ffffff" seed={1284} />
      <SkCircle cx={-19} cy={-3} r={3} fill="#0c4a6e" seed={1285} />
      <SkCircle cx={-19} cy={-4} r={1} fill="#ffffff" seed={1286} />
      {/* pectoral fin */}
      <SkEllipse cx={4} cy={8} rx={9} ry={5} fill="#0ea5e9" seed={1287} />
      {/* bubbles */}
      {[0, 1, 2].map((i) => {
        const bPhase = (t * 0.8 + i * 0.4) % 1;
        const bOpacity = bPhase < 0.8 ? 1 - bPhase : 0;
        return (
          <SkCircle
            key={i}
            cx={-28 - i * 5}
            cy={-16 - bPhase * 18}
            r={2 + i}
            fill="none"
            stroke="#7dd3fc"
            seed={1288 + i}
            opacity={bOpacity}
          />
        );
      })}
    </g>
  );
}

// ─── skBird ──────────────────────────────────────────────────────────────────
/** Small bird with flapping wings and a perch bob. Seed range 1300–1319. */
export function skBird(x: number, y: number, t: number, scale = 1, color = "#facc15"): React.ReactNode {
  const flapAngle = Math.sin(t * 8) * 28;   // wing flap
  const bob = Math.sin(t * 4) * 2;
  return (
    <g transform={`translate(${x} ${y + bob}) scale(${scale})`}>
      {/* left wing */}
      <g transform={`rotate(${-flapAngle} 0 0)`}>
        <SkEllipse cx={-16} cy={-2} rx={16} ry={8} fill={color} seed={1300} />
      </g>
      {/* right wing */}
      <g transform={`rotate(${flapAngle} 0 0)`}>
        <SkEllipse cx={16} cy={-2} rx={16} ry={8} fill={color} seed={1301} />
      </g>
      {/* body */}
      <SkEllipse cx={0} cy={4} rx={14} ry={12} fill={color} seed={1302} />
      {/* tail feathers */}
      <SkPolygon points={[[6, 14], [0, 28], [-6, 14]]} fill="#ca8a04" seed={1303} />
      {/* head */}
      <SkCircle cx={0} cy={-8} r={11} fill={color} seed={1304} />
      {/* beak */}
      <SkPolygon points={[[-13, -8], [-20, -6], [-13, -4]]} fill="#fb923c" seed={1305} />
      {/* eye */}
      <SkCircle cx={-4} cy={-10} r={3} fill="#1c1917" seed={1306} />
      <SkCircle cx={-4} cy={-11} r={1} fill="#ffffff" seed={1307} />
      {/* breast patch */}
      <SkEllipse cx={0} cy={6} rx={7} ry={8} fill="#fed7aa" seed={1308} />
    </g>
  );
}

// ─── skBee ───────────────────────────────────────────────────────────────────
/** Bee with buzzing wings and a little wobble flight path. Seed range 1320–1339. */
export function skBee(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const wingBuzz = Math.sin(t * 20) * 12;   // rapid wing beat
  const wobbleX = Math.sin(t * 2.3) * 6;   // lazy flight drift
  const wobbleY = Math.cos(t * 3.1) * 4;
  return (
    <g transform={`translate(${x + wobbleX} ${y + wobbleY}) scale(${scale})`}>
      {/* wings — buzz very fast */}
      <g transform={`rotate(${wingBuzz} 0 -4)`}>
        <SkEllipse cx={-14} cy={-10} rx={13} ry={7} fill="#e0f2fe" seed={1320} opacity={0.85} />
      </g>
      <g transform={`rotate(${-wingBuzz} 0 -4)`}>
        <SkEllipse cx={14} cy={-10} rx={13} ry={7} fill="#e0f2fe" seed={1321} opacity={0.85} />
      </g>
      {/* abdomen — yellow/black stripes via stacked rects */}
      <SkEllipse cx={8} cy={6} rx={10} ry={8} fill="#fbbf24" seed={1322} />
      <SkRect x={0} y={0} w={16} h={5} fill="#1c1917" seed={1323} opacity={0.5} />
      <SkRect x={0} y={7} w={16} h={5} fill="#1c1917" seed={1324} opacity={0.5} />
      {/* body */}
      <SkEllipse cx={-4} cy={2} rx={14} ry={10} fill="#fbbf24" seed={1325} />
      {/* head */}
      <SkCircle cx={-16} cy={0} r={8} fill="#fde047" seed={1326} />
      {/* eyes */}
      <SkCircle cx={-20} cy={-2} r={2} fill="#1c1917" seed={1327} />
      {/* stinger */}
      <SkPolygon points={[[17, 4], [24, 6], [17, 8]]} fill="#78350f" seed={1328} />
      {/* antennae */}
      <SkLine x1={-16} y1={-8} x2={-22} y2={-18} seed={1329} />
      <SkLine x1={-14} y1={-8} x2={-8} y2={-18} seed={1330} />
      <SkCircle cx={-22} cy={-18} r={2} fill="#fde047" seed={1331} />
      <SkCircle cx={-8} cy={-18} r={2} fill="#fde047" seed={1332} />
    </g>
  );
}

// ─── skSnail ─────────────────────────────────────────────────────────────────
/** Snail with a spiral shell (approximated by concentric ellipses), antennae wiggle. Seed 1340–1359. */
export function skSnail(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const antennaWiggle = Math.sin(t * 3) * 6;   // antennae sway
  const eyeWiggle = Math.cos(t * 3) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* body / foot */}
      <SkEllipse cx={0} cy={8} rx={32} ry={12} fill="#86efac" seed={1340} />
      {/* neck / head protrusion */}
      <SkEllipse cx={-24} cy={2} rx={10} ry={8} fill="#86efac" seed={1341} />
      {/* shell — three concentric ellipses give the illusion of a spiral */}
      <SkEllipse cx={10} cy={-8} rx={24} ry={20} fill="#fb923c" seed={1342} />
      <SkEllipse cx={10} cy={-8} rx={16} ry={13} fill="#fbbf24" seed={1343} />
      <SkEllipse cx={10} cy={-8} rx={8} ry={6} fill="#fb923c" seed={1344} />
      <SkCircle cx={10} cy={-8} r={3} fill="#fde047" seed={1345} />
      {/* head */}
      <SkCircle cx={-28} cy={-4} r={9} fill="#4ade80" seed={1346} />
      {/* antennae — pivot from head top */}
      <g transform={`translate(-28 -12) rotate(${antennaWiggle})`}>
        <SkLine x1={-4} y1={0} x2={-10} y2={-14} seed={1347} />
        <SkCircle cx={-10} cy={-14} r={3} fill="#1c1917" seed={1348} />
      </g>
      <g transform={`translate(-28 -12) rotate(${-antennaWiggle})`}>
        <SkLine x1={4} y1={0} x2={10} y2={-14} seed={1349} />
        <SkCircle cx={10 + eyeWiggle * 0.2} cy={-14} r={3} fill="#1c1917" seed={1350} />
      </g>
      {/* smile */}
      <SkLine x1={-32} y1={0} x2={-26} y2={2} seed={1351} />
    </g>
  );
}
